// The phone's settings page: the legacy MobileSettings.
//
// The general view — dark mode, language, the default stand-in and the office,
// each saved the moment it is chosen — then the links under "Tutorial",
// "Support", "Rechtliches" and "Technisches", and signing out. "Persönliche
// Daten" opens the second view with the fields an administrator keeps, and the
// mailbox PIN.

import { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Autocomplete,
  Box,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Switch,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import LogoutIcon from "@mui/icons-material/Logout";
import { darken, lighten } from "polished";
import { api, ApiError, type ProxyOption } from "~/api";
import { useBootstrap, useRefresh } from "~/bootstrap";
import { setLanguage, useLang, useT, type Lang } from "~/i18n";
import { setDark, useThemeState } from "~/theme";
import { signOut } from "~/layout";
import ForwardIcon from "~/legacy/icons/ForwardIcon";
import { digits, menuProps, notifyLabel, StyledTextField, useProxies, VERSION } from "./usersettings";

const text16 = { fontSize: "16px", fontWeight: 400, lineHeight: "22px", letterSpacing: "-0.43px" } as const;
const heading20 = { mt: 1.5, fontSize: "20px", fontWeight: 700, lineHeight: "28px", letterSpacing: "-0.43px" } as const;

export function SettingsPage() {
  const theme = useTheme();
  const t = useT();
  const navigate = useNavigate();
  const [switchView, setSwitchView] = useState(false);

  useEffect(() => {
    document.title = t("vmm-settings");
    document.body.style.backgroundColor = theme.palette.background.default;
    return () => {
      document.body.style.backgroundColor = "";
    };
  }, [t, theme.palette.background.default]);

  return (
    <Fragment>
      <Grid container sx={{ pl: 1, pb: 1.5, borderBottom: 1, borderBottomColor: "primary.main" }}>
        <IconButton
          onClick={() => navigate("/")}
          sx={{ mt: -3.5, position: "relative", zIndex: theme.zIndex.appBar + 101, display: switchView ? "none" : "inline-flex" }}
        >
          <ArrowBackIosNewIcon sx={{ color: "primary.main", fontSize: "28px" }} />
        </IconButton>
        <Typography
          sx={{
            ml: 0.5,
            mt: -3,
            fontSize: "30px",
            fontWeight: 700,
            lineHeight: "34px",
            letterSpacing: "-0.75px",
            position: "relative",
            zIndex: theme.zIndex.appBar + 101,
          }}
        >
          {t("settings")}
        </Typography>
      </Grid>
      {switchView ? <PersonalView onBack={() => setSwitchView(false)} /> : <GeneralView onPersonal={() => setSwitchView(true)} />}
    </Fragment>
  );
}

function useLabelStyle() {
  const theme = useTheme();
  return {
    fontSize: "13px",
    fontWeight: 400,
    lineHeight: "16px",
    letterSpacing: "-0.75px",
    color: theme.palette.mode === "light" ? lighten(0.15, theme.palette.text.primary) : darken(0.12, theme.palette.text.primary),
  };
}

function GeneralView({ onPersonal }: { onPersonal: () => void }) {
  const theme = useTheme();
  const t = useT();
  const lang = useLang();
  const b = useBootstrap();
  const refresh = useRefresh();
  const { dark } = useThemeState();
  const proxies = useProxies();
  const label = useLabelStyle();

  const [proxy, setProxy] = useState(b.me.favouriteProxy?.id ?? 0);
  const [office, setOffice] = useState(b.me.favouriteSecretariat?.id ?? 0);
  const [notify, setNotify] = useState(b.me.notifyDefault);

  // A choice is saved the moment it is made: the legacy page posted
  // /proxy/{email} on change and had no save button for it.
  const patch = async (body: Record<string, unknown>) => {
    try {
      await api("/api/portal/me", { method: "PATCH", body });
      refresh();
    } catch {
      refresh();
    }
  };

  const users = proxies.data?.users ?? [];
  const secretariats = proxies.data?.secretariats ?? [];
  const find = (list: ProxyOption[], id: number) => list.find((o) => o.id === id);

  return (
    <Fragment>
      <Grid container direction="column" sx={{ pb: 1, borderBottom: 1, borderBottomColor: "primary.main" }}>
        <Typography sx={{ ml: 2, mt: 1.5, ...label }}>{t("presentation")}</Typography>
        <Grid container sx={{ my: 1 }}>
          <Box onClick={() => setDark(!dark)} sx={{ ml: 1.5, display: "inherit", justifyContent: "inherit" }}>
            <Switch checked={dark} color="primary" size="small" />
            <Typography sx={{ ml: 0.5, ...text16 }}>{t("dark-mode")}</Typography>
          </Box>
        </Grid>
        <Typography sx={{ ml: 2, mt: 0.5, ...label }}>{t("language")}</Typography>
        <StyledTextField
          variant="outlined"
          size="small"
          value={lang}
          onChange={(e) => setLanguage(e.target.value as Lang)}
          select
          SelectProps={menuProps}
          sx={{ px: 1.8, my: 1 }}
        >
          <MenuItem dense value="en" style={{ whiteSpace: "normal" }}>
            {t("english")}
          </MenuItem>
          <MenuItem dense value="de" style={{ whiteSpace: "normal" }}>
            {t("german")}
          </MenuItem>
        </StyledTextField>
        <Typography sx={{ ml: 2, mt: 1, ...label }}>{t("default-proxy")}</Typography>
        {users.length < 20 ? (
          <StyledTextField
            variant="outlined"
            size="small"
            value={find(users, proxy) ? proxy : ""}
            onChange={(e) => {
              const id = Number(e.target.value);
              setProxy(id);
              void patch({ favouriteProxyId: id });
            }}
            select
            InputLabelProps={{ shrink: true }}
            SelectProps={menuProps}
            sx={{ px: 1.8, my: 1 }}
          >
            {users.map((r) => (
              <MenuItem dense key={r.id} value={r.id}>
                <Typography>{r.name}</Typography>
              </MenuItem>
            ))}
          </StyledTextField>
        ) : (
          <Autocomplete
            options={users}
            getOptionLabel={(o) => o.name}
            value={find(users, proxy)}
            onChange={(_, o) => {
              if (!o) return;
              setProxy(o.id);
              void patch({ favouriteProxyId: o.id });
            }}
            renderInput={(params) => (
              <StyledTextField {...params} variant="outlined" size="small" InputLabelProps={{ shrink: true }} sx={{ px: 1.8, my: 1 }} />
            )}
            disableClearable
            isOptionEqualToValue={(o, v) => o.id === v?.id}
          />
        )}
        <Typography sx={{ ml: 2, mt: 1, ...label }}>{t("virtual-proxy")}</Typography>
        <StyledTextField
          variant="outlined"
          size="small"
          value={find(secretariats, office) ? office : ""}
          onChange={(e) => {
            const id = Number(e.target.value);
            setOffice(id);
            void patch({ favouriteSecretariatId: id });
          }}
          select
          InputLabelProps={{ shrink: true }}
          SelectProps={menuProps}
          sx={{ px: 1.8, my: 1 }}
        >
          {secretariats.map((r) => (
            <MenuItem dense key={r.id} value={r.id}>
              <Typography>{r.name}</Typography>
            </MenuItem>
          ))}
        </StyledTextField>
        {b.company.notifyChannel !== "none" ? (
          <>
            <Typography sx={{ ml: 2, mt: 1, ...label }}>{t("notification-type")}</Typography>
            <StyledTextField
              variant="outlined"
              size="small"
              value={notify}
              onChange={(e) => {
                setNotify(e.target.value);
                void patch({ notifyDefault: e.target.value });
              }}
              select
              InputLabelProps={{ shrink: true }}
              SelectProps={menuProps}
              sx={{ px: 1.8, my: 1 }}
            >
              {Object.entries(notifyLabel).map(([value, key]) => (
                <MenuItem dense key={value} value={value} style={{ whiteSpace: "normal" }}>
                  {t(key)}
                </MenuItem>
              ))}
            </StyledTextField>
          </>
        ) : null}
        <IconButton onClick={onPersonal} sx={{ pl: 2, pr: 1.5, py: 0, my: 1, width: "100%", borderRadius: 6, justifyContent: "flex-start" }}>
          <Typography sx={{ ...text16, color: theme.palette.text.primary, ml: 0.5 }}>{t("personal-data")}</Typography>
          <Box sx={{ flexGrow: 1 }} />
          <ForwardIcon />
        </IconButton>
      </Grid>
      <Grid container direction="column" sx={{ px: 2, pb: 1 }}>
        <Typography sx={{ ...heading20, color: theme.palette.text.primary }}>Tutorial</Typography>
        <Link href="https://mobilboxmanager.info/mobilboxmanager_kurzanleitung/" sx={{ mt: 1, ...text16 }}>
          {t("short-instructions")}
        </Link>
        <Link href="https://www.youtube.com/watch?v=kyzdjQCixIw" sx={{ mt: 1, ...text16 }}>
          {t("product-video")}
        </Link>
      </Grid>
      <Grid container direction="column" sx={{ px: 2, pb: 1 }}>
        <Typography sx={{ ...heading20, color: theme.palette.text.primary }}>Support</Typography>
        <Typography sx={{ mt: 1, ...text16 }}>{t("contact-support-text")}</Typography>
      </Grid>
      <Grid container direction="column" sx={{ px: 2, pb: 1 }}>
        <Typography sx={{ ...heading20, color: theme.palette.text.primary }}>{t("legal")}</Typography>
        <Link href="https://mobilboxmanager.info/impressum/" sx={{ mt: 1, ...text16 }}>
          {t("imprint")}
        </Link>
        <Link href="https://mobilboxmanager.info/datenschutz/" sx={{ mt: 1, ...text16 }}>
          {t("data-protection")}
        </Link>
        <Link href="https://www.gosh.at/" sx={{ mt: 1, ...text16 }}>
          Powered by gosh!
        </Link>
      </Grid>
      <Grid container direction="column" sx={{ px: 2, pb: 1 }}>
        <Typography sx={{ ...heading20, color: theme.palette.text.primary }}>{t("technical")}</Typography>
        <Typography sx={{ mt: 1, ...text16 }}>Version {VERSION}</Typography>
      </Grid>
      <Grid container direction="column" sx={{ px: 2, pb: 4, alignContent: "flex-start" }}>
        <Grid container onClick={() => void signOut()} sx={{ mt: 3 }}>
          <LogoutIcon sx={{ color: "error.main", fontSize: 18, mt: 0.25 }} />
          <Typography sx={{ color: "error.main", ml: 0.5, ...text16 }}>{t("logout")}</Typography>
        </Grid>
      </Grid>
    </Fragment>
  );
}

function PersonalView({ onBack }: { onBack: () => void }) {
  const theme = useTheme();
  const t = useT();
  const b = useBootstrap();
  const refresh = useRefresh();
  const label = useLabelStyle();
  const below732 = useMediaQuery("(max-height: 732px)");

  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    setLoading(true);
    setError("");
    try {
      await api("/api/portal/me/mailbox-pin", { method: "PUT", body: { pin } });
      setPin("");
      refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : t("something-went-wrong"));
    } finally {
      setLoading(false);
    }
  };

  const readOnly = (key: string, value: string) => (
    <>
      <Typography sx={{ ml: 2, mt: key === "Email" ? 2 : 1.5, ...label }}>{key === "Email" ? "Email" : t(key)}</Typography>
      <Tooltip title={t("contact-an-admin")} followCursor>
        <StyledTextField variant="outlined" size="small" value={value} sx={{ mx: 2, mt: 1 }} disabled />
      </Tooltip>
    </>
  );

  const unchanged = pin === "";

  return (
    <Grid container direction="column" sx={{ height: "100%", mt: 1, pb: 1 }}>
      <Grid container>
        <Box onClick={onBack} sx={{ display: "inherit", justifyContent: "inherit" }}>
          <IconButton sx={{ position: "relative", zIndex: theme.zIndex.appBar + 101 }}>
            <ArrowBackIosNewIcon sx={{ color: "primary.main", fontSize: "18px" }} />
          </IconButton>
          <Typography sx={{ pt: 0.4, fontSize: "20px", fontWeight: 700, lineHeight: "28px", letterSpacing: "-0.43px", color: theme.palette.text.primary }}>
            {t("personal-data")}
          </Typography>
        </Box>
      </Grid>
      {readOnly("Email", b.me.email || " ")}
      {readOnly("first-name", b.me.firstName || " ")}
      {readOnly("surname", b.me.lastName || " ")}
      {readOnly("phone", b.me.phone || "")}
      {readOnly("vpn-number", b.me.vpn || "")}
      <Typography sx={{ ml: 2, mt: 1.5, ...label }}>{t("voicemail-password")}</Typography>
      <StyledTextField
        variant="outlined"
        size="small"
        placeholder={b.me.hasMailboxPin ? "••••" : ""}
        value={pin}
        onChange={(e) => setPin(digits(e.target.value))}
        inputProps={{ inputMode: "numeric", pattern: "[0-9]*", autoComplete: "off" }}
        sx={{ mx: 2, mt: 1, mb: 2 }}
      />
      {error ? (
        <Typography role="alert" sx={{ color: "error.main", fontSize: 14, mx: 2, mb: 1 }}>
          {error}
        </Typography>
      ) : null}
      <Grid container direction="column" sx={{ px: 2, alignItems: "flex-end" }}>
        <Box sx={{ flexGrow: 1 }} />
        <LoadingButton
          variant="contained"
          disabled={unchanged || loading}
          loading={loading}
          onClick={save}
          sx={{
            width: below732 ? "45%" : "40%",
            position: below732 ? "relative" : "absolute",
            bottom: below732 ? 0 : 20,
            right: below732 ? 0 : 20,
            mb: below732 ? 2 : 0,
            backgroundColor: theme.palette.mode === "dark" ? "primary.dark" : "primary.main",
            textTransform: "none",
            border: 1,
            borderColor: unchanged || loading ? "action.hover" : "primary.main",
            borderRadius: 6,
            "&:hover": { backgroundColor: "primary.dark" },
            "&:disabled": { backgroundColor: "action.disabledBackground" },
          }}
        >
          <Typography
            sx={{ color: unchanged ? "text.primary" : "primary.contrastText", fontSize: 16, fontWeight: unchanged ? 400 : 700, visibility: loading ? "hidden" : "visible" }}
          >
            {t("save")}
          </Typography>
        </LoadingButton>
      </Grid>
    </Grid>
  );
}
